import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { usePageMeta } from '../hooks/usePageMeta';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Briefcase,
  ShieldCheck,
  Users2,
  Globe2,
  TrendingUp,
  Mail,
  CalendarCheck,
  CheckCircle2,
} from 'lucide-react';

export const ServicesCompaniesPage = () => {
  const { t } = useLanguage();
  usePageMeta('servicesCompanies.title', 'seo.companiesDescription');

  const services = [
    {
      icon: Briefcase,
      title: t('servicesCompanies.s1Title'),
      description: t('servicesCompanies.s1Desc'),
      features: t('servicesCompanies.s1Features'),
    },
    {
      icon: ShieldCheck,
      title: t('servicesCompanies.s2Title'),
      description: t('servicesCompanies.s2Desc'),
      features: t('servicesCompanies.s2Features'),
    },
    {
      icon: Users2,
      title: t('servicesCompanies.s3Title'),
      description: t('servicesCompanies.s3Desc'),
      features: t('servicesCompanies.s3Features'),
    },
    {
      icon: Globe2,
      title: t('servicesCompanies.s4Title'),
      description: t('servicesCompanies.s4Desc'),
      features: t('servicesCompanies.s4Features'),
    },
  ];

  const benefits = [
    t('servicesCompanies.benefit1'),
    t('servicesCompanies.benefit2'),
    t('servicesCompanies.benefit3'),
    t('servicesCompanies.benefit4'),
    t('servicesCompanies.benefit5'),
  ];

  const steps = [
    { num: '01', title: t('servicesCompanies.step1Title'), desc: t('servicesCompanies.step1Desc') },
    { num: '02', title: t('servicesCompanies.step2Title'), desc: t('servicesCompanies.step2Desc') },
    { num: '03', title: t('servicesCompanies.step3Title'), desc: t('servicesCompanies.step3Desc') },
    { num: '04', title: t('servicesCompanies.step4Title'), desc: t('servicesCompanies.step4Desc') },
  ];

  const quoteSubject = encodeURIComponent(t('servicesCompanies.quoteEmailSubject'));
  const quoteBody = encodeURIComponent(t('servicesCompanies.quoteEmailBody'));

  return (
    <div className="min-h-screen" data-testid="services-companies-page">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-sea-blue/5 via-white to-bronze/5 py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block bg-bronze/15 text-bronze text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              {t('servicesCompanies.audienceBadge')}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('servicesCompanies.title')}
            </h1>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              {t('servicesCompanies.intro1')}
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              {t('servicesCompanies.intro2')}
            </p>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('servicesCompanies.servicesHeading')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('servicesCompanies.servicesSub')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card
                  key={index}
                  className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-sea-blue/30"
                >
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-lg bg-bronze/15 flex items-center justify-center flex-shrink-0 group-hover:bg-bronze group-hover:scale-110 transition-all duration-300">
                        <Icon className="h-7 w-7 text-bronze group-hover:text-white transition-colors" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-2xl text-gray-900 mb-2">
                          {service.title}
                        </CardTitle>
                        <p className="text-gray-600 text-sm">{service.description}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-700">
                          <div className="w-1.5 h-1.5 rounded-full bg-sea-blue flex-shrink-0 mt-2" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why companies choose us */}
      <section className="py-20 bg-gradient-to-br from-bronze/5 via-white to-sea-blue/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sea-blue/10 mb-4">
                <TrendingUp className="h-8 w-8 text-sea-blue" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('servicesCompanies.whyTitle')}
              </h2>
              <p className="text-lg text-gray-600">{t('servicesCompanies.whySubtitle')}</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {benefits.map((b, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 bg-white rounded-lg p-5 shadow-sm border border-gray-100"
                >
                  <CheckCircle2 className="h-6 w-6 text-sea-blue flex-shrink-0 mt-0.5" />
                  <p className="text-gray-800 font-medium">{b}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('servicesCompanies.processTitle')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('servicesCompanies.processSubtitle')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {steps.map((step, idx) => (
              <div
                key={idx}
                className="relative bg-gradient-to-br from-sea-blue/5 to-bronze/5 rounded-lg p-6 border border-sea-blue/10"
              >
                <div className="text-5xl font-bold text-sea-blue/20 mb-2">{step.num}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-700 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing / Custom Quote */}
      <section className="py-20 bg-gradient-to-br from-sea-blue/5 via-white to-bronze/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <Card className="border-2 border-bronze/30 shadow-lg" data-testid="company-pricing-card">
              <CardHeader>
                <CardTitle className="text-3xl text-center text-gray-900">
                  {t('servicesCompanies.pricingTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-center text-gray-700 text-lg">
                  {t('servicesCompanies.pricingP1')}
                </p>
                <p className="text-center text-gray-700 text-lg">
                  {t('servicesCompanies.pricingP2')}
                </p>

                <div className="bg-white rounded-lg p-6 border border-sea-blue/20">
                  <h3 className="font-bold text-gray-900 mb-3">
                    {t('servicesCompanies.pricingIncludesTitle')}
                  </h3>
                  <ul className="space-y-2">
                    {[
                      t('servicesCompanies.pricingIncludes1'),
                      t('servicesCompanies.pricingIncludes2'),
                      t('servicesCompanies.pricingIncludes3'),
                      t('servicesCompanies.pricingIncludes4'),
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-700">
                        <CheckCircle2 className="h-5 w-5 text-bronze flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                  <a
                    href={`mailto:hello@nextstepdenmark.com?subject=${quoteSubject}&body=${quoteBody}`}
                    data-testid="request-quote-btn"
                  >
                    <Button
                      size="lg"
                      className="bg-bronze hover:bg-bronze/90 text-white w-full sm:w-auto"
                    >
                      <Mail className="mr-2 h-5 w-5" />
                      {t('servicesCompanies.requestQuote')}
                    </Button>
                  </a>
                  <Link to="/booking?type=corporate">
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-sea-blue text-sea-blue hover:bg-sea-blue hover:text-white w-full sm:w-auto"
                      data-testid="company-book-meeting-btn"
                    >
                      <CalendarCheck className="mr-2 h-5 w-5" />
                      {t('servicesCompanies.bookConsultation')}
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-sea-blue text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t('servicesCompanies.ctaTitle')}
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            {t('servicesCompanies.ctaSubtitle')}
          </p>
          <Link to="/booking?type=corporate">
            <Button size="lg" className="bg-bronze hover:bg-bronze/90 text-white">
              <CalendarCheck className="mr-2 h-5 w-5" />
              {t('servicesCompanies.ctaButton')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
